import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BsChevronDown } from 'react-icons/bs';
import InfiniteScroll from 'react-infinite-scroll-component';
import {
  Box,
  Button, Flex, Icon,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text, useDisclosure
} from '@chakra-ui/react';
import { useSelect } from 'downshift';
import { snakeCase } from 'lodash';
import PropTypes from 'prop-types';

import colors from '../../styles/theme/foundation/colors';
import { hoverStyle, menuButtonStyles } from '../MultiSelectDropdown/styles';
import LoadingSpinner from '../Spinner';
import TruncationTooltip from '../TruncationTooltip';

const InfiniteDropdownMenu = ({
  data,
  pageInfo,
  idKey,
  labelKey,
  initialSelect = null,
  scrollClassName,
  fetchMoreData,
  cypressId,
  onSelect = () => {},
  placeholder = 'None Selected',
  variant = 'secondary',
  isDisabled = false
}) => {
  const [selected, setSelected] = useState(initialSelect);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const initRef = useRef();

  let buttonStyles;

  if (variant === 'primary') {
    buttonStyles = {
      ...menuButtonStyles,
      backgroundColor: colors.greyScale['100'],
      color: selected ? colors.greyScale['900'] : colors.greyScale['500'],
      _hover: {
        backgroundColor: colors.greyScale['100'],
        color: colors.greyScale['500']
      }
    };
  } else if (variant === 'secondary') {
    buttonStyles = {
      ...menuButtonStyles,
      backgroundColor: colors.primaryScale['50'],
      color: colors.primaryScale['600'],
      fontWeight: '500',
      _hover: {
        backgroundColor: colors.greyScale['100'],
        color: colors.greyScale['500']
      }
    };
  }

  useEffect(() => {
    setSelected(initialSelect);
  }, [initialSelect]) // eslint-disable-line

  const mappedData = useMemo(() => data.map(el => ({
    id: el[idKey],
    label: el[labelKey],
    ...el
  })), [data]); // eslint-disable-line

  const {
    getToggleButtonProps,
    getMenuProps,
    highlightedIndex,
    getItemProps
  } = useSelect({
    items: mappedData,
    onSelectedItemChange: ({ selectedItem }) => {
      setSelected(selectedItem);
      onSelect(selectedItem);
      onClose();
    }
  });

  if (!data?.length && !pageInfo?.currentPage) {
    return (<LoadingSpinner />);
  }

  return (
    <Popover
      matchWidth
      initialFocusRef={initRef}
      isOpen={isOpen}
      width="100%"
      onClose={onClose}
      onOpen={onOpen}
    >
      <PopoverTrigger>
        <Button
          _active={{ color: 'primaryScale.600', borderColor: 'primaryScale.600', borderWidth: '1px' }}
          border={isOpen && `1px solid ${colors.primaryScale['600']}`}
          color={isOpen && colors.primaryScale['600']}
          data-cy={cypressId}
          isDisabled={isDisabled}
          rightIcon={<Icon as={BsChevronDown} />}
          style={buttonStyles}
          variant="unstyled"
          width="100%"
          {...getToggleButtonProps()}
        >
          {selected?.label || placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent {...getMenuProps({ ref: initRef })} width="inherit">
        <PopoverBody width="inherit">
          <Box className={scrollClassName} minW="205px">
            <InfiniteScroll
              dataLength={mappedData.length}
              hasMore={pageInfo.currentPage !== pageInfo.lastPage}
              height={mappedData.length < 6 ? 'auto' : '300px'}
              loader={(<LoadingSpinner isInline />)}
              next={fetchMoreData}
              pageInfo={pageInfo}
              scrollableTarget={scrollClassName}
              scrollThreshold={0.8}
            >
              {mappedData.map((el, index) => (
                <Flex
                  _hover={hoverStyle}
                  _selected={hoverStyle}
                  alignItems="center"
                  bgColor="white"
                  borderRadius="md"
                  color="greyScale.600"
                  cursor="pointer"
                  data-cy={`${cypressId}-option-${snakeCase(el.label)}`}
                  flexFlow="row nowrap"
                  height="40px"
                  key={el.id}
                  padding="10px 14px"
                  textStyle="bodyMd500"
                  textTransform="capitalize"
                  {...getItemProps({ item: el, index })}
                >
                  <TruncationTooltip label={el.label}>
                    <Text noOfLines={1}>{el.label}</Text>
                  </TruncationTooltip>
                </Flex>
              ))}
            </InfiniteScroll>
          </Box>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

InfiniteDropdownMenu.propTypes = {
  /** Data set to iterate over for list */
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  /** Page information to use for pagination and fetching more data */
  pageInfo: PropTypes.shape({
    currentPage: PropTypes.number,
    lastPage: PropTypes.number
  }).isRequired,
  /** Key for the value of the input */
  idKey: PropTypes.string.isRequired,
  /** Key for the text to be displayed for each list element */
  labelKey: PropTypes.string.isRequired,
  /** The initial object for initial render.  Must include id and label */
  initialSelect: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    label: PropTypes.string
  }),
  /** The class name used to reference for the infinite scroll */
  scrollClassName: PropTypes.string.isRequired,
  /** Function that fetches the next page of data */
  fetchMoreData: PropTypes.func.isRequired,
  /** Function used to  */
  onSelect: PropTypes.func,
  /** The text to show when nothing is selected */
  placeholder: PropTypes.string,
  cypressId: PropTypes.string
};

export default InfiniteDropdownMenu;
